import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";

import { getMyFacilityActions, type FacilityActions } from "@/libs/permissionsApi";
import { listAssignableClubCoaches } from "@/libs/staffApi";
import {
  reassignProgramInstructor,
  type UnifiedProgramResponse,
} from "@/libs/programsApi";

interface ProgramInstructorCardProps {
  program: UnifiedProgramResponse;
  onUpdated: () => Promise<unknown>;
}

export function ProgramInstructorCard({ program, onUpdated }: ProgramInstructorCardProps) {
  const { user, loading } = useAuth();
  const client = useQueryClient();
  const actorId = user?.uid;
  const scopeReady = Boolean(actorId && !loading && program.facility_id);
  const requiredAction = program.audience === "private_coaching"
    ? "facility:lesson_manage" : "facility:program";
  const facilityAuthorityKey = ["program-instructor-authority", actorId,
    program.facility_id, requiredAction] as const;
  const facilityAuthority = useQuery({
    queryKey: facilityAuthorityKey,
    queryFn: () => getMyFacilityActions(program.facility_id!),
    enabled: scopeReady,
    staleTime: 0, refetchOnMount: "always", retry: false,
  });
  const canManageInstructor = scopeReady && facilityAuthority.isSuccess
    && facilityAuthority.fetchStatus === "idle" && Array.isArray(facilityAuthority.data?.actions)
    && facilityAuthority.data.actions.includes(requiredAction);
  const scopeKey = JSON.stringify([actorId, program.program_id,
    program.facility_id, program.coach_profile_id, requiredAction]);
  const currentScope = useRef<string | null>(null);
  const mounted = useRef(false);
  currentScope.current = canManageInstructor ? scopeKey : null;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  function isCurrentScope(): boolean {
    const facility = client.getQueryState<FacilityActions>(facilityAuthorityKey);
    return mounted.current && currentScope.current === scopeKey
      && facility?.status === "success"
      && facility.fetchStatus === "idle" && !facility.isInvalidated
      && Array.isArray(facility.data?.actions)
      && facility.data.actions.includes(requiredAction);
  }

  if (!canManageInstructor || !actorId) return null;
  return <ProgramInstructorEditor key={scopeKey} program={program} onUpdated={onUpdated}
    actorId={actorId} isCurrentScope={isCurrentScope} />;
}

function ProgramInstructorEditor({ program, onUpdated, actorId, isCurrentScope }:
  ProgramInstructorCardProps & { actorId: string; isCurrentScope: () => boolean }) {
  const client = useQueryClient();
  const [selected, setSelected] = useState(program.coach_profile_id ?? "");
  const profilesKey = ["program-instructors", actorId, program.facility_id,
    program.audience] as const;
  const profiles = useQuery({
    queryKey: profilesKey,
    queryFn: ({ signal }) => listAssignableClubCoaches(program.facility_id!, signal),
    staleTime: 0, refetchOnMount: "always", retry: false,
  });
  const options = useMemo(() => (profiles.data ?? []).filter((profile) =>
    (profile.facilities ?? []).some((binding) =>
      binding.facility_id === program.facility_id && binding.status === "active")),
  [profiles.data, program.facility_id]);
  const assigned = options.find((profile) =>
    profile.coach_profile_id === program.coach_profile_id);
  const rosterReady = profiles.isSuccess && profiles.fetchStatus === "idle";
  const selectedEligible = options.some((profile) => profile.coach_profile_id === selected);
  const mutation = useMutation({
    mutationFn: (target: string) => {
      const roster = client.getQueryState(profilesKey);
      if (!isCurrentScope() || !rosterReady || roster?.status !== "success"
        || roster.fetchStatus !== "idle" || roster.isInvalidated || roster.data !== profiles.data
        || !options.some((profile) => profile.coach_profile_id === target)) {
        throw new Error("Coach information changed. Refresh and try again.");
      }
      return reassignProgramInstructor(program.program_id, target);
    },
    onSuccess: () => isCurrentScope() ? onUpdated() : undefined,
  });
  const canReassign = rosterReady && selectedEligible && selected !== program.coach_profile_id && !mutation.isPending;
  const assignment = !rosterReady ? null : assigned
    ? `${assigned.display_name} is assigned.`
    : program.coach_profile_id ? "Assigned coach details are unavailable."
    : "No coach is assigned.";
  return <Paper component="section" aria-label="Assigned coach" variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
    <Stack spacing={2}>
      <div><Typography component="h2" variant="h6">Coach</Typography>
        {assignment ? <Typography variant="body2" color="text.secondary">{assignment}</Typography> : null}
        <Typography variant="body2" color="text.secondary">Changing the coach updates future delivery only. The player’s purchase, accepted terms, and completed teaching history stay unchanged.</Typography></div>
      {profiles.isError ? <Alert severity="error">Couldn’t load eligible club coaches.</Alert> : null}
      {!rosterReady && !profiles.isError ? <Typography role="status">
        {profiles.fetchStatus === "paused" ? "Waiting for a connection to load eligible club coaches…" : "Loading eligible club coaches…"}
      </Typography> : null}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField select size="small" label="Club coach" value={selectedEligible ? selected : ""}
          disabled={!rosterReady || mutation.isPending}
          onChange={(event) => setSelected(event.target.value)} sx={{ minWidth: 260 }}>
          {options.map((profile) => <MenuItem key={profile.coach_profile_id} value={profile.coach_profile_id}>{profile.display_name}</MenuItem>)}
        </TextField>
        <Button size="small" variant="contained" disabled={!canReassign}
          onClick={() => { if (canReassign && isCurrentScope()) mutation.mutate(selected); }}>
          {mutation.isPending ? "Assigning…" : "Assign coach"}</Button>
      </Stack>
      {mutation.isError ? <Alert severity="error">Couldn’t assign this coach. Refresh the coach roster and try again.</Alert> : null}
    </Stack>
  </Paper>;
}
